/**
 * @boli/core - Payment Service
 *
 * Platform-agnostic payment logic for Boli Live.
 * This service is used by BOTH Web (Phase 1) and Mobile (Phase 2) with ZERO changes.
 *
 * Key Features:
 * - Safepay/PayMob tokenization (never store raw card data)
 * - Escrow management (7-day hold after delivery)
 * - COD support for orders < PKR 5,000
 * - Provincial Sales Tax (PST) calculation
 * - Bidding Passport (10 PKR auth-hold at session start)
 */

import type {
  PaymentMethod,
  Payment,
  PaymentStatus,
  EscrowTransaction,
  Province,
  ApiResponse,
} from '@boli/types';

// ============================================================================
// INTERFACES
// ============================================================================

export interface IPaymentService {
  /**
   * Add a new payment method (tokenized)
   */
  addPaymentMethod(cardDetails: CardTokenizationRequest): Promise<ApiResponse<PaymentMethod>>;

  /**
   * Get user's payment methods
   */
  getPaymentMethods(userId: string): Promise<PaymentMethod[]>;

  /**
   * Delete a payment method
   */
  deletePaymentMethod(paymentMethodId: string): Promise<ApiResponse<void>>;

  /**
   * Create bidding passport (10 PKR auth-hold)
   */
  createBiddingPassport(userId: string, paymentMethodId: string): Promise<ApiResponse<BiddingPassport>>;

  /**
   * Authorize payment for auction win
   */
  authorizePayment(orderId: string, amount: number): Promise<ApiResponse<Payment>>;

  /**
   * Capture authorized payment (after cooling-off period)
   */
  capturePayment(paymentId: string): Promise<ApiResponse<Payment>>;

  /**
   * Release escrow to seller
   */
  releaseEscrow(escrowId: string): Promise<ApiResponse<EscrowTransaction>>;

  /**
   * Request refund
   */
  requestRefund(paymentId: string, reason: string): Promise<ApiResponse<Payment>>;

  /**
   * Calculate total with PST
   */
  calculateTotal(bidAmount: number, shippingCost: number, sellerProvince: Province): PaymentBreakdown;

  /**
   * Check if COD is available
   */
  isCODAvailable(totalAmount: number, buyerLogisticsScore: number): boolean;
}

export interface CardTokenizationRequest {
  cardNumber: string;
  expiryMonth: number;
  expiryYear: number;
  cvv: string;
  holderName: string;
  saveCard: boolean;
}

export interface BiddingPassport {
  id: string;
  userId: string;
  paymentMethodId: string;
  authorizationId: string;
  amount: number; // 10 PKR
  status: 'active' | 'expired' | 'used';
  expiresAt: string;
  createdAt: string;
}

export interface PaymentBreakdown {
  bidAmount: number;
  shippingCost: number;
  pstAmount: number;
  pstRate: number;
  totalAmount: number;
  sellerProvince: Province;
}

export interface PaymentServiceConfig {
  apiBaseUrl: string;
  gatewayProvider: 'safepay' | 'paymob';
  biddingPassportAmount: number; // PKR
  codMaxAmount: number; // PKR
  escrowHoldDays: number;
  coolingOffMinutes: number;
}

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_PAYMENT_CONFIG: PaymentServiceConfig = {
  apiBaseUrl: '',
  gatewayProvider: 'safepay',
  biddingPassportAmount: 10, // 10 PKR
  codMaxAmount: 5000, // PKR
  escrowHoldDays: 7,
  coolingOffMinutes: 30,
};

// ============================================================================
// PST RATES (Pakistan Provincial Sales Tax)
// ============================================================================

const PST_RATES: Record<Province, number> = {
  sindh: 0.13, // SRB - Sindh Revenue Board
  punjab: 0.16, // PRA - Punjab Revenue Authority
  kpk: 0.15,
  balochistan: 0.15,
  islamabad: 0.16,
  gilgit: 0.00,
  ajk: 0.00,
};

// ============================================================================
// PAYMENT SERVICE IMPLEMENTATION
// ============================================================================

export class PaymentService implements IPaymentService {
  private config: PaymentServiceConfig;
  private fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>;

  constructor(
    config: Partial<PaymentServiceConfig>,
    fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>
  ) {
    this.config = { ...DEFAULT_PAYMENT_CONFIG, ...config };
    this.fetchAdapter = fetchAdapter;
  }

  /**
   * Add a new payment method (tokenized via gateway)
   * NOTE: Card data is sent directly to gateway, never to our servers
   */
  async addPaymentMethod(
    cardDetails: CardTokenizationRequest
  ): Promise<ApiResponse<PaymentMethod>> {
    try {
      // Step 1: Get tokenization URL from our backend
      const setupResponse = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/payments/setup-tokenization`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            saveCard: cardDetails.saveCard,
          }),
        }
      );

      if (!setupResponse.ok) {
        return {
          success: false,
          error: {
            code: 'SETUP_FAILED',
            message: 'Failed to setup card tokenization',
          },
        };
      }

      const setupData = await setupResponse.json();

      // Step 2: Tokenize card with payment gateway
      // In production, this would be done via the gateway's SDK/iframe
      // We simulate it here for the architecture
      const tokenResponse = await this.fetchAdapter(setupData.tokenizationUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          card_number: cardDetails.cardNumber,
          expiry_month: cardDetails.expiryMonth,
          expiry_year: cardDetails.expiryYear,
          cvv: cardDetails.cvv,
          holder_name: cardDetails.holderName,
        }),
      });

      if (!tokenResponse.ok) {
        return {
          success: false,
          error: {
            code: 'TOKENIZATION_FAILED',
            message: 'Failed to tokenize card',
          },
        };
      }

      const tokenData = await tokenResponse.json();

      // Step 3: Save tokenized payment method to our backend
      const saveResponse = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/payments/methods`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            gatewayToken: tokenData.token,
            lastFour: cardDetails.cardNumber.slice(-4),
            brand: this.detectCardBrand(cardDetails.cardNumber),
            expiryMonth: cardDetails.expiryMonth,
            expiryYear: cardDetails.expiryYear,
          }),
        }
      );

      if (!saveResponse.ok) {
        return {
          success: false,
          error: {
            code: 'SAVE_FAILED',
            message: 'Failed to save payment method',
          },
        };
      }

      const paymentMethod = await saveResponse.json();
      return { success: true, data: paymentMethod };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Get user's payment methods
   */
  async getPaymentMethods(userId: string): Promise<PaymentMethod[]> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/payments/methods`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch payment methods');
      }

      const data = await response.json();
      return data.methods || [];
    } catch (error) {
      console.error('Failed to get payment methods:', error);
      return [];
    }
  }

  /**
   * Delete a payment method
   */
  async deletePaymentMethod(paymentMethodId: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/payments/methods/${paymentMethodId}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: 'DELETE_FAILED',
            message: 'Failed to delete payment method',
          },
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Create bidding passport (10 PKR auth-hold at session start)
   * This validates the card is real and has funds
   */
  async createBiddingPassport(
    userId: string,
    paymentMethodId: string
  ): Promise<ApiResponse<BiddingPassport>> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/payments/bidding-passport`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentMethodId,
            amount: this.config.biddingPassportAmount,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: {
            code: errorData.code || 'PASSPORT_FAILED',
            message: errorData.message || 'Failed to create bidding passport',
          },
        };
      }

      const passport = await response.json();
      return { success: true, data: passport };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Authorize payment for auction win
   */
  async authorizePayment(
    orderId: string,
    amount: number
  ): Promise<ApiResponse<Payment>> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/payments/authorize`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId, amount }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: {
            code: errorData.code || 'AUTH_FAILED',
            message: errorData.message || 'Failed to authorize payment',
          },
        };
      }

      const payment = await response.json();
      return { success: true, data: payment };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Capture authorized payment (after cooling-off period)
   */
  async capturePayment(paymentId: string): Promise<ApiResponse<Payment>> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/payments/${paymentId}/capture`,
        { method: 'POST' }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: {
            code: errorData.code || 'CAPTURE_FAILED',
            message: errorData.message || 'Failed to capture payment',
          },
        };
      }

      const payment = await response.json();
      return { success: true, data: payment };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Release escrow to seller
   */
  async releaseEscrow(escrowId: string): Promise<ApiResponse<EscrowTransaction>> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/escrow/${escrowId}/release`,
        { method: 'POST' }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: {
            code: errorData.code || 'RELEASE_FAILED',
            message: errorData.message || 'Failed to release escrow',
          },
        };
      }

      const escrow = await response.json();
      return { success: true, data: escrow };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Request refund
   */
  async requestRefund(paymentId: string, reason: string): Promise<ApiResponse<Payment>> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/payments/${paymentId}/refund`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: {
            code: errorData.code || 'REFUND_FAILED',
            message: errorData.message || 'Failed to request refund',
          },
        };
      }

      const payment = await response.json();
      return { success: true, data: payment };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Calculate total with PST
   */
  calculateTotal(
    bidAmount: number,
    shippingCost: number,
    sellerProvince: Province
  ): PaymentBreakdown {
    const pstRate = PST_RATES[sellerProvince] || 0;
    const pstAmount = Math.round(bidAmount * pstRate);
    const totalAmount = bidAmount + shippingCost + pstAmount;

    return {
      bidAmount,
      shippingCost,
      pstAmount,
      pstRate,
      totalAmount,
      sellerProvince,
    };
  }

  /**
   * Check if COD is available
   */
  isCODAvailable(totalAmount: number, buyerLogisticsScore: number): boolean {
    // COD only for orders under threshold
    if (totalAmount > this.config.codMaxAmount) {
      return false;
    }

    // COD not available for users with poor logistics score (high RTO rate)
    if (buyerLogisticsScore < 50) {
      return false;
    }

    return true;
  }

  /**
   * Detect card brand from number
   */
  private detectCardBrand(cardNumber: string): 'visa' | 'mastercard' | 'other' {
    const cleaned = cardNumber.replace(/\D/g, '');
    if (cleaned.startsWith('4')) return 'visa';
    if (/^5[1-5]/.test(cleaned) || /^2[2-7]/.test(cleaned)) return 'mastercard';
    return 'other';
  }

  /**
   * Get escrow release date (7 days after delivery)
   */
  getEscrowReleaseDate(deliveryDate: Date): Date {
    const releaseDate = new Date(deliveryDate);
    releaseDate.setDate(releaseDate.getDate() + this.config.escrowHoldDays);
    return releaseDate;
  }

  /**
   * Check if cooling-off period has passed
   */
  isCoolingOffComplete(auctionEndTime: Date): boolean {
    const now = new Date();
    const coolingOffEnd = new Date(auctionEndTime);
    coolingOffEnd.setMinutes(coolingOffEnd.getMinutes() + this.config.coolingOffMinutes);
    return now >= coolingOffEnd;
  }
}

// ============================================================================
// PAYMENT SERVICE FACTORY
// ============================================================================

let paymentServiceInstance: PaymentService | null = null;

export function createPaymentService(
  config: Partial<PaymentServiceConfig>,
  fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>
): PaymentService {
  paymentServiceInstance = new PaymentService(config, fetchAdapter);
  return paymentServiceInstance;
}

export function getPaymentService(): PaymentService {
  if (!paymentServiceInstance) {
    throw new Error('PaymentService not initialized. Call createPaymentService first.');
  }
  return paymentServiceInstance;
}
